import { useQuery } from '@tanstack/react-query'
import { apiFetch } from '../api/apiFetch'
import { useAuth } from './useAuth'

export type MembershipRole = 'SuperAdmin' | 'Admin' | 'Staff' | 'User'

export interface Membership {
  districtId: string
  role: MembershipRole
}

export interface CurrentUserDto {
  id: string
  email: string
  displayName: string
  memberships: Membership[]
  impersonating: string | null
  // TODO(viltrapport-port phase 3): backend does not yet return activeSeasonId on this endpoint
  activeSeasonId?: string
}

const ROLE_RANK: Record<MembershipRole, number> = {
  SuperAdmin: 3,
  Admin: 2,
  Staff: 1,
  User: 0,
}

// SuperAdmin satisfies every lower role; Admin satisfies Admin/Staff/User; etc.
// A payload without memberships (an impersonation answer, an older API) holds no role.
export function hasRole(user: CurrentUserDto | undefined, role: MembershipRole): boolean {
  if (!user || !Array.isArray(user.memberships)) return false
  const required = ROLE_RANK[role]
  return user.memberships.some((m) => ROLE_RANK[m.role] >= required)
}

/** The user's role in one district, or undefined when not a member (or no district). Mirrors clubhouse's. */
export function roleInDistrict(
  user: CurrentUserDto | undefined,
  districtId: string | null | undefined,
): MembershipRole | undefined {
  if (!user || !Array.isArray(user.memberships) || !districtId) return undefined
  return user.memberships.find((m) => m.districtId === districtId)?.role
}

/** Whether the user holds at least `required` in the given district. Mirrors clubhouse's. */
export function hasRoleInDistrict(
  user: CurrentUserDto | undefined,
  districtId: string | null | undefined,
  required: MembershipRole,
): boolean {
  const role = roleInDistrict(user, districtId)
  return role !== undefined && ROLE_RANK[role] >= ROLE_RANK[required]
}

export function useCurrentUser() {
  const { state } = useAuth()
  return useQuery({
    queryKey: ['authentication'],
    queryFn: async () => {
      if (state.status !== 'signed-in') throw new Error('Not signed in')
      const token = await state.user.getIdToken()
      // Call apiFetch directly (not useApiFetch) to avoid a circular dependency:
      // useApiFetch depends on useCurrentUser to get the district id, so
      // useCurrentUser must not depend on useApiFetch in return.
      // The /api/v1/authentication endpoint is district-agnostic and returns
      // memberships for all districts — it does not need X-District-Id.
      return apiFetch<CurrentUserDto>('/api/v1/authentication', { token })
    },
    enabled: state.status === 'signed-in',
    staleTime: Infinity,
  })
}
